import React, { useState, useEffect, useRef } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import { Modal, Button, Form } from 'react-bootstrap';
import { useCheckAccess } from "../../utils/Utilities";
import '../../pages/general/General.css';
import { Navbar, Footer, Loading, Breadcrumbs } from "../../components/shared";
import { Helmet } from "react-helmet-async";

import {
  fetchEvents,
  createEvent,
  updateEvent,
  getEventById,
  deleteEvent,
} from "../../service/EventsApi";

const AdminCalendar = () => {
  useCheckAccess("EventsRW");

  const [events, setEvents] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [currentEvent, setCurrentEvent] = useState(null);
  const [modalMode, setModalMode] = useState("view");
  const [loading, setLoading] = useState(true);
  const calendarRef = useRef(null);
  const descriptionRef = useRef(null);
  const publicNotesRef = useRef(null);
  const hiddenNotesRef = useRef(null);
  const [sponsors, setSponsors] = useState([]);


  useEffect(() => {
    loadEvents();
  }, []);

  useEffect(() => {
    adjustTextareaHeight(descriptionRef);
    adjustTextareaHeight(publicNotesRef);
    adjustTextareaHeight(hiddenNotesRef);
  }, [
    currentEvent?.description,
    currentEvent?.publicNotes,
    currentEvent?.hiddenNotes
  ]);

  const adjustTextareaHeight = (ref) => {
    if (ref?.current) {
      ref.current.style.height = 'auto';
      ref.current.style.height = `${ref.current.scrollHeight}px`;
    }
  };

  const dateToFirestoreTimestamp = (date) => {
    const d = new Date(date);
    return {
      _seconds: Math.floor(d.getTime() / 1000),
      _nanoseconds: (d.getTime() % 1000) * 1e6,
    };
  };

  const loadEvents = async () => {
    setLoading(true);
    const result = await fetchEvents();
    if (result) {
      const formatted = result.map(event => {
        const start = new Date(event.start._seconds * 1000);
        const end = new Date(event.end._seconds * 1000);

        return {
          id: event.id || event.docId || '',
          title: event.title || '',
          description: event.description || '',
          publicNotes: event.publicNotes || '',
          hiddenNotes: event.hiddenNotes || '',
          start,
          end,
          sponsors: event.sponsors || []
        };
      });
      setEvents(formatted);
    }
    setLoading(false);
  };

  const handleDateClick = (arg) => {
    setCurrentEvent({
      title: '',
      description: '',
      publicNotes: '',
      hiddenNotes: '',
      start: arg.dateStr + "T00:00",
      end: arg.dateStr + "T01:00",
      repeatDaily: false,
      recurrenceStart: '',
      recurrenceEnd: '',
    });
    setModalMode("edit");
    setShowModal(true);
  };

  const handleEventClick = async (arg) => {
    const eventId = arg.event.id;
    const eventData = await getEventById(eventId);

    if (eventData) {
      const utcToIST = (utcDate) => {
        const istOffset = 5.5 * 60 * 60 * 1000;
        return new Date(utcDate.getTime() + istOffset);
      };

      const startUTC = new Date(eventData.start._seconds * 1000);
      const endUTC = new Date(eventData.end._seconds * 1000);

      const startIST = utcToIST(startUTC);
      const endIST = utcToIST(endUTC);

      const formatForInput = (date) => date.toISOString().slice(0, 16);

      setCurrentEvent({
        ...eventData,
        id: eventId,
        title: eventData.title || '',
        description: eventData.description || '',
        publicNotes: eventData.publicNotes || '',
        hiddenNotes: eventData.hiddenNotes || '',
        start: formatForInput(startIST),
        end: formatForInput(endIST),
        repeatDaily: false,
        recurrenceStart: '',
        recurrenceEnd: '',
      });
      setSponsors(eventData.sponsors || []);

      setModalMode("view");
      setShowModal(true);
    }
  };

  const handleSaveEvent = async () => {
    if (!currentEvent?.title || !currentEvent?.start || !currentEvent?.end) {
      alert("அனைத்து புலங்களும் நிரப்பப்பட வேண்டும்.");
      return;
    }

    const startDate = new Date(currentEvent.start);
    const endDate = new Date(currentEvent.end);

    if (startDate >= endDate) {
      alert("தொடக்க நேரம் முடிவு நேரத்தைக் காட்டிலும் குறைவாக இருக்க வேண்டும்.");
      return;
    }

    const isRecurring = currentEvent.repeatDaily &&
      currentEvent.recurrenceStart &&
      currentEvent.recurrenceEnd;

    let success = false;

    if (!currentEvent.id && isRecurring) {
      const recurrenceStart = new Date(currentEvent.recurrenceStart);
      const recurrenceEnd = new Date(currentEvent.recurrenceEnd);

      if (recurrenceStart > recurrenceEnd) {
        alert("மீண்டும் தொடக்கம் தேதி, முடிவு தேதியைவிட முன்னதாக இருக்க வேண்டும்.");
        return;
      }

      const eventsToCreate = [];

      for (let d = new Date(recurrenceStart); d <= recurrenceEnd; d.setDate(d.getDate() + 1)) {
        const startTime = new Date(currentEvent.start);
        const endTime = new Date(currentEvent.end);

        startTime.setFullYear(d.getFullYear(), d.getMonth(), d.getDate());
        endTime.setFullYear(d.getFullYear(), d.getMonth(), d.getDate());

        if (startTime >= endTime) {
          alert("ஒவ்வொரு நாளுக்கும் தொடக்கம் நேரம் முடிவு நேரத்தைக் காட்டிலும் குறைவாக இருக்க வேண்டும்.");
          return;
        }

        eventsToCreate.push({
          title: currentEvent.title,
          description: currentEvent.description,
          publicNotes: currentEvent.publicNotes,
          hiddenNotes: currentEvent.hiddenNotes,
          start: dateToFirestoreTimestamp(startTime),
          end: dateToFirestoreTimestamp(endTime),
        });
      }

      for (const event of eventsToCreate) {
        await createEvent(event);
      }

      success = true;
    } else {
      const eventData = {
        title: currentEvent.title,
        description: currentEvent.description,
        publicNotes: currentEvent.publicNotes,
        hiddenNotes: currentEvent.hiddenNotes,
        start: dateToFirestoreTimestamp(currentEvent.start),
        end: dateToFirestoreTimestamp(currentEvent.end),
      };

      if (modalMode === "edit" && currentEvent.id) {
        success = await updateEvent(currentEvent.id, eventData);
      } else {
        success = await createEvent(eventData);
      }
    }

    if (success) {
      await loadEvents();
      setShowModal(false);
    }
  };

  const handleDeleteEvent = async () => {
    if (!currentEvent?.id) return;

    const confirmed = window.confirm("இந்த நிகழ்வை நிச்சயமாக நீக்க விரும்புகிறீர்களா?");
    if (!confirmed) return;

    const success = await deleteEvent(currentEvent.id);
    if (success) {
      await loadEvents();
      setShowModal(false);
    }
  };

  return (

    <>
      <Navbar />
      <Helmet>
        <title>ஸ்ரீ பொன்னாச்சி அம்மன் திருக்கோவில்கள் | கோயில் நிகழ்வுகள் நிர்வாகி</title>
        <link rel="canonical" href="/donation/create" />
      </Helmet>
      {loading ? (
        <div className="general_parent">
          <div className="general_container">
            <div className="general_body">
              <Breadcrumbs /><br />
              <h2>கோயில் நிகழ்வுகள்</h2 >
              <Loading />
            </div>
          </div>
        </div>
      ) : (<div className="general_parent">
        <div className="general_container">
          <div className="general_body">

            <h2>கோயில் நிகழ்வுகள்</h2 >
            <br />
            <div className="responsive-calendar">
              <FullCalendar
                ref={calendarRef}
                plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
                initialView="dayGridMonth"
                headerToolbar={{
                  left: 'prev,next today',
                  center: 'title',
                  right: 'dayGridMonth,timeGridWeek,timeGridDay',
                }}
                events={events}
                dateClick={handleDateClick}
                eventClick={handleEventClick}
                height="auto"
                nowIndicator={true}
              />
            </div>
            <Modal show={showModal} onHide={() => setShowModal(false)}>
              <Modal.Header closeButton>
                <Modal.Title>
                  {modalMode === "view" ? "நிகழ்வு" :
                    currentEvent?.id ? "நிகழ்வு திருத்து" : "புதிய நிகழ்வு உருவாக்கு"}
                </Modal.Title>
              </Modal.Header>
              <Modal.Body>
                <Form>
                  <Form.Group className="mb-3">
                    <Form.Label>தலைப்பு</Form.Label>
                    <Form.Control
                      type="text"
                      value={currentEvent?.title || ''}
                      disabled={modalMode === "view"}
                      onChange={(e) => setCurrentEvent({ ...currentEvent, title: e.target.value })}
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>விளக்கம்</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={4}
                      ref={descriptionRef}
                      style={{ overflow: 'hidden', resize: 'none' }}
                      value={currentEvent?.description || ''}
                      disabled={modalMode === "view"}
                      onChange={(e) => setCurrentEvent({ ...currentEvent, description: e.target.value })}
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>பொது குறிப்புகள்</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={4}
                      ref={publicNotesRef}
                      style={{ overflow: 'hidden', resize: 'none' }}
                      value={currentEvent?.publicNotes || ''}
                      disabled={modalMode === "view"}
                      onChange={(e) => setCurrentEvent({ ...currentEvent, publicNotes: e.target.value })}
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>மறைவு குறிப்புகள்</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={4}
                      ref={hiddenNotesRef}
                      style={{ overflow: 'hidden', resize: 'none' }}
                      value={currentEvent?.hiddenNotes || ''}
                      disabled={modalMode === "view"}
                      onChange={(e) => setCurrentEvent({ ...currentEvent, hiddenNotes: e.target.value })}
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>தொடக்கம்</Form.Label>
                    <Form.Control
                      type="datetime-local"
                      value={currentEvent?.start || ''}
                      disabled={modalMode === "view"}
                      onChange={(e) => setCurrentEvent({ ...currentEvent, start: e.target.value })}
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>நிறைவு</Form.Label>
                    <Form.Control
                      type="datetime-local"
                      value={currentEvent?.end || ''}
                      disabled={modalMode === "view"}
                      onChange={(e) => setCurrentEvent({ ...currentEvent, end: e.target.value })}
                    />
                  </Form.Group>

                  {(modalMode !== "view" && !currentEvent?.id) && (
                    <>
                      <Form.Group className="mb-3">
                        <Form.Check
                          type="checkbox"
                          label="Repeat Days"
                          checked={currentEvent?.repeatDaily || false}
                          onChange={(e) => setCurrentEvent({ ...currentEvent, repeatDaily: e.target.checked })}
                        />
                      </Form.Group>

                      {currentEvent?.repeatDaily && (
                        <>
                          <Form.Group className="mb-3">
                            <Form.Label>மீண்டும் தொடக்கம் தேதி</Form.Label>
                            <Form.Control
                              type="date"
                              value={currentEvent?.recurrenceStart || ''}
                              onChange={(e) => setCurrentEvent({ ...currentEvent, recurrenceStart: e.target.value })}
                            />
                          </Form.Group>

                          <Form.Group className="mb-3">
                            <Form.Label>மீண்டும் முடிவு தேதி</Form.Label>
                            <Form.Control
                              type="date"
                              value={currentEvent?.recurrenceEnd || ''}
                              onChange={(e) => setCurrentEvent({ ...currentEvent, recurrenceEnd: e.target.value })}
                            />
                          </Form.Group>
                        </>
                      )}
                    </>
                  )}
                </Form>
                {modalMode === "view" && sponsors.length > 0 && (
                  <>
                    <hr />
                    <h5>🎁 நன்கொடையாளர்கள்</h5>
                    <ul className="list-group">
                      {sponsors.map((s, idx) => (
                        <li key={idx} className="list-group-item">
                          👤 {s.familyMemberFirstName} (குடும்ப எண்: {s.familyId})<br />
                          📞 {s.familyMemberMobile || ''}{s.familyMemberMobile2 ? ` / ${s.familyMemberMobile2}` : ''}<br />
                          ₹{s.amount.toLocaleString()} - {new Date(s.transactionDate._seconds * 1000).toLocaleDateString('ta-IN')}
                          <br />
                          ✍️ {s.createdBy}
                        </li>
                      ))}
                    </ul>
                  </>
                )}

              </Modal.Body>

              <Modal.Footer>
                <Button variant="secondary" onClick={() => setShowModal(false)}>மூடு</Button>

                {modalMode === "view" && (
                  <>
                    <Button variant="warning" onClick={() => setModalMode("edit")}>திருத்து</Button>
                    <Button
                      variant="info"
                      onClick={() => {
                        setCurrentEvent({ ...currentEvent, id: null });
                        setModalMode("duplicate");
                      }}
                    >
                      நகலெடு
                    </Button>
                  </>
                )}

                {(modalMode === "edit" || modalMode === "duplicate") && (
                  <>
                    {modalMode === "edit" && currentEvent?.id && (
                      <Button variant="danger" onClick={handleDeleteEvent}>நீக்கு</Button>
                    )}
                    <Button variant="primary" onClick={handleSaveEvent}>சேமி</Button>
                  </>
                )}
                {(modalMode !== "view" || modalMode === "view") && currentEvent?.id && (
                  <div className="mb-3 text-center">
                    <a
                      href={`/donation/create?eventId=${currentEvent.id}`}
                      rel="noopener noreferrer"
                      className="btn btn-outline-success"
                    >
                      🎁 கட்டளைதாரர்கள் சேர்க்கவும்
                    </a>
                  </div>
                )}

              </Modal.Footer>
            </Modal>
            <br />
            <h4>நிகழ்வுகள் பட்டியல்</h4><br />
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>தலைப்பு</th>
                    <th>குறிப்புகள்</th>
                  </tr>
                </thead>
                <tbody>
                  {events
                    .filter((event) => {
                      const now = new Date();
                      const oneYearAgo = new Date();
                      oneYearAgo.setDate(now.getDate() - 365);
                      return event.start >= oneYearAgo;
                    })
                    .sort((a, b) => b.start - a.start)
                    .map((event) => (
                      <tr key={event.id}>
                        <td>
                          <b>{event.title}</b>
                          <div>தொடக்கம்: {event.start.toLocaleString('ta-IN')}</div>
                          முடிவு: {event.end.toLocaleString('ta-IN')}
                        </td>
                        <td>
                          <b>பொது குறிப்புகள்:</b>
                          {event.publicNotes?.trim() || '—'}
                          <br />
                          <b> மறைவு குறிப்புகள்:</b>
                          {event.hiddenNotes?.trim() || '—'}
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </div >
        </div >
      </div >
      )}
      <Footer />
    </>
  );
};

export default AdminCalendar;
